import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Observable, Subscription, catchError, debounceTime, map, of, startWith, switchMap } from 'rxjs';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { ApiMotorCalculoEnum } from '@shared/enums/formula/api-motor-calculo.enum';
import { TipoDadoVariavelEnum } from '@shared/enums/formula/tipo-dado-variavel.enum';
import { FormulaCardModoEnum } from '@shared/enums/motor-fiscal/formula-card-modo.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { MaskService } from '@shared/services/mask.service';
import { MotorCalculoService as MotorFiscalMotorCalculoService } from '@shared/services/apis/motor-fiscal/motor-calculo.service';
import { MotorCalculoService as SuprimentosMotorCalculoService } from '@shared/services/apis/suprimentos/motor-calculo.service';
import { CalculoFormulaEntradaRequest } from '@shared/services/requests/formula/calculo-formula-entrada.request';
import { CalculoFormulaSimularRequest } from '@shared/services/requests/formula/calculo-formula-simular.request';
import { CalculoFormulaSimularResponse } from '@shared/services/responses/formula/calculo-formula-simular.response';
import { CalculoFormulaSimulacaoEntradaStruct } from '@shared/services/structs/formula/calculo-formula-simulacao-entrada.struct';
import { CalculoFormulaSimulacaoSaidaStruct } from '@shared/services/structs/formula/calculo-formula-simulacao-saida.struct';
import { FormulaItemStruct } from '@shared/services/structs/formula/formula-item.struct';
import { FormulaCardComponent } from '../formula-card/formula-card.component';
import { FormulaSimulacaoDialogData } from './formula-simulacao-dialog-data';
import { FORMULA_BADGE_CLASSES } from '../utils/formula-badge.util';

export { FormulaSimulacaoDialogData, FormulaSimulacaoEntradaInicial } from './formula-simulacao-dialog-data';

/**
 * Simulação genérica de fórmulas: a API descobre as entradas do contexto (variáveis referenciadas pelas
 * fórmulas que não são saída do próprio contexto) e devolve as saídas em ordem de execução. O modal não
 * conhece DIFAL, custo nem venda; a tela informa a API, o contexto, as fórmulas em edição e os valores iniciais.
 */
@Component({
    selector: 'app-formula-simulacao-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatExpansionModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSlideToggleModule,
        NgxCurrencyDirective,
        AlertCardComponent,
        EmptyStateComponent,
        LoadingBoxComponent,
        FormulaCardComponent,
    ],
    templateUrl: './formula-simulacao-dialog.component.html',
    styleUrl: './formula-simulacao-dialog.component.css',
})
export class FormulaSimulacaoDialogComponent implements OnInit, OnDestroy {
    public readonly badgeClasses = FORMULA_BADGE_CLASSES;
    public readonly modoListagem = FormulaCardModoEnum.Listagem;
    public readonly TipoDadoVariavelEnum = TipoDadoVariavelEnum;
    public readonly decimalMask = this.maskService.getDecimalMask();
    public readonly integerMask = this.maskService.getIntegerMask();

    /** Um controle por entrada descoberta, indexado pela chave da variável (as chaves têm pontos; não usar `get`). */
    public readonly form = new FormGroup<Record<string, FormControl<number | boolean | null>>>({});
    public entradas: CalculoFormulaSimulacaoEntradaStruct[] = [];
    public saidas: CalculoFormulaSimulacaoSaidaStruct[] = [];
    public isCalculando = false;

    private subscription?: Subscription;

    constructor(
        public dialogRef: MatDialogRef<FormulaSimulacaoDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: FormulaSimulacaoDialogData,
        private maskService: MaskService,
        private alertService: AlertService,
        private motorFiscalMotorCalculoService: MotorFiscalMotorCalculoService,
        private suprimentosMotorCalculoService: SuprimentosMotorCalculoService,
    ) { }

    ngOnInit(): void {
        // A primeira chamada usa os valores iniciais da tela e descobre os campos; as seguintes
        // reenviam o formulário a cada alteração, cancelando o cálculo anterior ainda em voo.
        this.subscription = this.form.valueChanges.pipe(
            debounceTime(300),
            map(() => this.montarEntradasDoFormulario()),
            startWith(this.montarEntradasIniciais()),
            switchMap(entradas => this.simular(entradas)),
        ).subscribe(response => this.aplicarResultado(response));
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    public estadoDe(chaveFormula: string): FormulaItemStruct | null {
        return this.data.estados.find(estado => estado.chaveFormula === chaveFormula) ?? null;
    }

    public controle(entrada: CalculoFormulaSimulacaoEntradaStruct): FormControl<number | boolean | null> {
        return this.form.controls[entrada.chaveVariavel];
    }

    public fechar(): void {
        this.dialogRef.close();
    }

    private simular(entradas: CalculoFormulaEntradaRequest[]): Observable<CalculoFormulaSimularResponse | null> {
        this.isCalculando = true;

        const request = new CalculoFormulaSimularRequest();
        request.contextoRaiz = this.data.contextoRaiz;
        request.formulas = this.data.estados.map(estado => ({
            chaveFormula: estado.chaveFormula,
            tipo: estado.tipo,
            chaveFormulaModelo: estado.chaveFormulaModelo ?? null,
            expressao: estado.expressao ?? null,
            tipoArredondamento: estado.tipoArredondamento ?? null,
            casasDecimais: estado.casasDecimais ?? null,
        }));
        request.ciclosEmpresa = this.data.ciclosEmpresa ?? [];
        request.entradas = entradas;

        const chamada = this.data.api === ApiMotorCalculoEnum.Suprimentos
            ? this.suprimentosMotorCalculoService.simular(request)
            : this.motorFiscalMotorCalculoService.simular(request);
        return chamada.pipe(catchError(() => of(null)));
    }

    private aplicarResultado(response: CalculoFormulaSimularResponse | null): void {
        this.isCalculando = false;

        // A descoberta das entradas precede o cálculo na API. Mesmo se os valores iniciais
        // impedirem o cálculo, mantenha os campos para o usuário corrigir e tentar novamente.
        if (response) this.sincronizarEntradas(response.entradas);

        if (!response || response.isError) {
            this.alertService.show(
                'Erro',
                response?.errorDescription || 'Não foi possível simular o cálculo.',
                AlertType.error,
            );
            this.saidas = this.saidas.map(saida => ({ ...saida, valor: 0 }));
            return;
        }

        this.saidas = response.saidas;
    }

    /** Cria os controles das entradas descobertas; o conjunto é fixo para o contexto, mas chaves novas são absorvidas. */
    private sincronizarEntradas(entradas: CalculoFormulaSimulacaoEntradaStruct[]): void {
        const primeiraResposta = this.entradas.length === 0;
        for (const entrada of entradas) {
            if (this.form.controls[entrada.chaveVariavel]) continue;
            const inicial = this.data.entradasIniciais.find(item => item.chaveVariavel === entrada.chaveVariavel);
            const valor = inicial ? inicial.valor : entrada.valorUsado;
            this.form.addControl(
                entrada.chaveVariavel,
                new FormControl<number | boolean | null>(valor),
                { emitEvent: false },
            );
        }
        if (primeiraResposta || entradas.length !== this.entradas.length) {
            this.entradas = entradas;
        }
    }

    private montarEntradasIniciais(): CalculoFormulaEntradaRequest[] {
        return this.data.entradasIniciais.map(inicial => {
            const entrada = new CalculoFormulaEntradaRequest();
            entrada.chaveVariavel = inicial.chaveVariavel;
            if (typeof inicial.valor === 'boolean') entrada.valorBooleano = inicial.valor;
            else entrada.valorDecimal = inicial.valor;
            return entrada;
        });
    }

    private montarEntradasDoFormulario(): CalculoFormulaEntradaRequest[] {
        return this.entradas.map(item => {
            const valor = this.form.controls[item.chaveVariavel]?.value ?? null;
            const entrada = new CalculoFormulaEntradaRequest();
            entrada.chaveVariavel = item.chaveVariavel;
            switch (item.tipoDadoVariavel) {
                case TipoDadoVariavelEnum.Booleano:
                    entrada.valorBooleano = valor === true;
                    break;
                case TipoDadoVariavelEnum.Inteiro:
                    entrada.valorInteiro = typeof valor === 'number' ? valor : 0;
                    break;
                default:
                    entrada.valorDecimal = typeof valor === 'number' ? valor : 0;
                    break;
            }
            return entrada;
        });
    }
}
